import React, { useState } from 'react';
import { Outlet } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Avatar,
  Badge,
  Chip,
  Menu,
  MenuItem,
  ListItemIcon,
  Divider,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import LogoutIcon from '@mui/icons-material/Logout';
import NotificationsIcon from '@mui/icons-material/Notifications';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import GroupsIcon from '@mui/icons-material/Groups';
import { useAuth } from '../../context/AuthContext';
import Sidebar from './Sidebar';
import LanguageSwitcher from './LanguageSwitcher';

const DRAWER_WIDTH = 260;

function getInitials(email) {
  if (!email) return '?';
  const parts = email.split('@')[0].split(/[._-]/);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[1][0]).toUpperCase();
  }
  return email.substring(0, 2).toUpperCase();
}

const AdminLayout = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [userMenuAnchor, setUserMenuAnchor] = useState(null);
  const { logout, adminProfile } = useAuth();
  const { t } = useTranslation('layout');
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const role = adminProfile?.role;
  const roleLabel = role ? t(`roles.${role}`, { defaultValue: role }) : t('roles.default');
  const initials = getInitials(adminProfile?.email);
  const displayName = adminProfile?.email?.split('@')[0] || t('roles.default');

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <Sidebar
        drawerWidth={DRAWER_WIDTH}
        mobileOpen={mobileOpen}
        onClose={handleDrawerToggle}
        isDesktop={isDesktop}
      />

      {/* Main content area (AppBar + page) */}
      <Box
        sx={{
          flexGrow: 1,
          width: { lg: `calc(100% - ${DRAWER_WIDTH}px)` },
          ml: { lg: `${DRAWER_WIDTH}px` },
          display: 'flex',
          flexDirection: 'column',
          minHeight: '100vh',
        }}
      >
        <AppBar
          position="sticky"
          elevation={0}
          sx={{
            bgcolor: 'white',
            borderBottom: '1px solid',
            borderColor: 'grey.200',
          }}
        >
          <Toolbar>
            {!isDesktop && (
              <IconButton
                edge="start"
                onClick={handleDrawerToggle}
                aria-label={t('openMenu')}
                sx={{ mr: 2, color: 'text.primary' }}
              >
                <MenuIcon />
              </IconButton>
            )}

            {/* Left: Role view indicator */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Avatar
                sx={{
                  bgcolor: 'rgba(99, 91, 255, 0.1)',
                  width: 38,
                  height: 38,
                }}
              >
                <GroupsIcon sx={{ color: '#635bff', fontSize: 22 }} />
              </Avatar>
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Typography
                    variant="subtitle1"
                    sx={{ fontWeight: 700, color: 'text.primary', lineHeight: 1.3 }}
                  >
                    {t('roleView', { role: roleLabel })}
                  </Typography>
                </Box>
                <Typography variant="caption" sx={{ color: 'text.secondary', lineHeight: 1 }}>
                  {t('appName')}
                  <KeyboardArrowDownIcon sx={{ fontSize: 14, verticalAlign: 'middle', ml: 0.25 }} />
                </Typography>
              </Box>
            </Box>

            <Box sx={{ flexGrow: 1 }} />

            {/* Right: Language, notifications + user profile */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <LanguageSwitcher />

              <IconButton
                aria-label={t('notifications')}
                sx={{
                  color: 'text.secondary',
                  bgcolor: 'grey.100',
                  '&:hover': { bgcolor: 'grey.200' },
                }}
              >
                <Badge badgeContent={4} color="error" sx={{ '& .MuiBadge-badge': { fontSize: 10, height: 18, minWidth: 18 } }}>
                  <NotificationsIcon sx={{ fontSize: 20 }} />
                </Badge>
              </IconButton>

              <Chip
                avatar={
                  <Avatar sx={{ bgcolor: '#635bff', width: 34, height: 34, fontSize: '0.8rem', fontWeight: 600 }}>
                    {initials}
                  </Avatar>
                }
                label={
                  <Box sx={{ display: { xs: 'none', md: 'flex' }, flexDirection: 'column', alignItems: 'flex-start', ml: 0.5 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.3, color: 'text.primary' }}>
                      {displayName}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary', lineHeight: 1, fontSize: '0.7rem' }}>
                      {roleLabel}
                    </Typography>
                  </Box>
                }
                deleteIcon={<KeyboardArrowDownIcon sx={{ fontSize: 18 }} />}
                onDelete={(e) => setUserMenuAnchor(e.currentTarget)}
                onClick={(e) => setUserMenuAnchor(e.currentTarget)}
                variant="outlined"
                sx={{
                  height: 'auto',
                  py: 0.5,
                  pl: 0.5,
                  pr: 1,
                  borderRadius: 8,
                  borderColor: 'grey.200',
                  cursor: 'pointer',
                  '& .MuiChip-label': { px: 0.5 },
                  '& .MuiChip-deleteIcon': { color: 'text.secondary' },
                  '&:hover': { borderColor: 'grey.400', bgcolor: 'grey.50' },
                }}
              />

              <Menu
                anchorEl={userMenuAnchor}
                open={Boolean(userMenuAnchor)}
                onClose={() => setUserMenuAnchor(null)}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                slotProps={{ paper: { sx: { mt: 1, minWidth: 200, borderRadius: 2 } } }}
              >
                <Box sx={{ px: 2, py: 1.5 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>{displayName}</Typography>
                  <Typography variant="caption" color="text.secondary">{adminProfile?.email}</Typography>
                </Box>
                <Divider />
                <MenuItem onClick={() => { setUserMenuAnchor(null); logout(); }} sx={{ py: 1.5 }}>
                  <ListItemIcon><LogoutIcon fontSize="small" /></ListItemIcon>
                  {t('logout')}
                </MenuItem>
              </Menu>
            </Box>
          </Toolbar>
        </AppBar>

        {/* Page content */}
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            p: 3,
            bgcolor: '#f8fafc',
          }}
        >
          <Outlet />
        </Box>
      </Box>
    </Box>
  );
};

export default AdminLayout;
